import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { match, useParams } from 'react-router-dom';
import { components as commonComponents, hooks as commonHooks } from '@sitebuilder/common';
import { StateShape as ReduxStateShape } from './reducers';
import { actionCreators } from './reducers/templateEditor';
import { actionCreators as compActionCreator } from './reducers/components';
import { actionCreators as themesActionCreator } from './reducers/themes';
import { actionCreators as resourcesActionCreators } from './reducers/resources';
import EditorRootDocument from './components/EditorRootDocument';

interface PropsShape {
    match: match<{
        documentName: string;
        componentId: string;
        libraryName: string;
        themeId: string;
    }>;
}

export const EditorPageViewer = (props: PropsShape) => {
    const params: any = useParams();
    const appContext = commonHooks.useAppContext();
    const { locale } = params;
    const {
        data,
        document: doc,
        documentType,
        colorPalettes,
        documentMetadata,
        accessToken,
        theme: selectedTheme,
        originalHostname,
        baseUrl,
        url,
        siteResourceStrings,
    } = useSelector((state: ReduxStateShape) => {
        return {
            selectedId: state.templateEditor.selectedId,
            data: state.templateEditor.data,
            document: state.templateEditor.document,
            documentType: state.app.documentType,
            documentMetadata: state.templateEditor.documentMetadata,
            accessToken: state.auth?.user?.access_token,
            theme: state.themes.theme,
            colorPalettes: state.themes.colorPalettes,
            originalHostname: state.app.originalHostname,
            baseUrl: state.app.baseUrl,
            url: state.app.url,
            isComponentLoaded: state.components.isComponentLoaded,
            editor: state.templateEditor.editor,
            siteResourceStrings: state.resources?.resources?.[locale || 'en-US'],
        };
    });

    const dispatch = useDispatch();
    const { match } = props;
    const { documentName, componentId, libraryName, themeId } = match.params;

    useEffect(() => {
        if (documentName) {
            dispatch(actionCreators.loadDocument(documentName));
        }

        if (componentId && libraryName) {
            dispatch(actionCreators.setEditor('componentEditor'));
            dispatch(compActionCreator.loadComponent(libraryName, componentId));
        }

        if (themeId) {
            dispatch(themesActionCreator.loadTheme(themeId));
        }
        dispatch(resourcesActionCreators.getResources());

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentName, componentId, libraryName]);

    return (
        <commonComponents.AppContext.AppContextProvider
            {...{
                ...appContext,
                isClient: true,
                data: {
                    ...data,
                    resourceStrings: { ...siteResourceStrings, ...data?.resourceStrings },
                },
                pageName: documentName,
                componentName: componentId,
                document: doc,
                documentType,
                colorPalettes,
                documentMetadata,
                inEditMode: false,
                accessToken,
                theme: selectedTheme,
                originalHostname,
                baseUrl,
                url,
            }}
        >
            <EditorRootDocument />
        </commonComponents.AppContext.AppContextProvider>
    );
};
